import { defineMessages, type MessageDescriptor } from 'react-intl';

export type MemoryModalMessageKey =
	| 'backButton'
	| 'closeButton'
	| 'title'
	| 'subtitle'
	| 'observationsSubtitle'
	| 'explicitMemorySubtitle'
	| 'profileMemoryTab'
	| 'profileMemoryEmptyHeading'
	| 'profileMemoryEmptyMessage'
	| 'profileMemoryEmptyButton'
	| 'profileMemoryErrorMessage'
	| 'explicitFactsTab'
	| 'loadingLabel'
	| 'deleteSuccessMessage'
	| 'deleteErrorMessage'
	| 'manageMemoryEdits'
	| 'editBlurbButton'
	| 'editBlurbInputPlaceholder'
	| 'submitEditBlurb'
	| 'addMemoryButton'
	| 'addMemoryHelperText'
	| 'MemorySubscreenFooterAddMemoryPlaceholder'
	| 'submitAddMemory'
	| 'cancelAddMemory'
	| 'confirmAddMemory'
	| 'addSuccessMessage'
	| 'addErrorMessage';

export const messages: Record<MemoryModalMessageKey, MessageDescriptor> = defineMessages({
	backButton: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.back-button',
		defaultMessage: 'Rovo memory',
		description: 'Label for the back button in the tabbed memory panel header',
	},
	closeButton: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.close-button',
		defaultMessage: 'Close',
		description: 'Accessible label for the close button in the tabbed memory panel header',
	},
	title: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.title',
		defaultMessage: 'Rovo memory',
		description: 'Title of the tabbed memory panel',
	},
	subtitle: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.subtitle',
		defaultMessage: 'This summary is updated weekly based on your Teamwork Graph activity.',
		description: 'Subtitle of the tabbed memory panel describing how often the summary updates',
	},
	observationsSubtitle: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.observations-subtitle',
		defaultMessage: 'This summary is updated weekly based on your Teamwork Graph activity.',
		description:
			'Subtitle shown in the memory modal header when the observations (profile memory) tab is selected, describing how often the summary updates',
	},
	explicitMemorySubtitle: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.explicit-memory-subtitle',
		defaultMessage: 'All explicit facts that you told to Rovo about yourself.',
		description:
			'Subtitle shown in the memory modal header when the explicit memory tab is selected, describing the explicit facts the user shared with Rovo',
	},
	profileMemoryTab: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.profile-memory-tab',
		defaultMessage: "Rovo's observations about you",
		description: "Label for the tab showing Rovo's profile memory narrative about the user",
	},
	profileMemoryEmptyHeading: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.profile-memory-empty-heading',
		defaultMessage: 'Let’s build some memories',
		description: 'Heading shown when Rovo has not yet generated any profile memory for the user',
	},
	profileMemoryEmptyButton: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.profile-memory-empty-button',
		defaultMessage: 'Share a fact with Rovo',
		description: 'Button label to open Rovo chat and share a fact when profile memory is empty',
	},
	profileMemoryEmptyMessage: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.profile-memory-empty-message',
		defaultMessage:
			'Tell Rovo a few facts about your role or your team in Rovo Chat. You can also work across your Atlassian apps and Rovo will start learning about you.',
		description: 'Message shown when Rovo has not yet generated any profile memory for the user',
	},
	profileMemoryErrorMessage: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.profile-memory-error-message',
		defaultMessage: "Rovo couldn't load your memory summary. Please try again later.",
		description: 'Message shown when the profile memory content fails to load',
	},
	explicitFactsTab: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.explicit-facts-tab',
		defaultMessage: "Information you've shared directly",
		description: 'Label for the tab showing information the user has shared directly with Rovo',
	},
	loadingLabel: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.loading-profile-memories-label',
		defaultMessage: 'Loading',
		description: 'Label to be read by screen readers when profile memories are loading',
	},
	deleteSuccessMessage: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.delete-success-message',
		defaultMessage: 'Memory successfully removed.',
		description: 'Flag message shown after a memory is successfully deleted',
	},
	deleteErrorMessage: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.delete-error-message',
		defaultMessage: "We couldn't delete this memory. Please try again.",
		description: 'Flag message shown when deleting a memory fails',
	},
	manageMemoryEdits: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.manage-memory-edits',
		defaultMessage: 'Manage memory edits',
		description:
			'Label for the footer row button that navigates to the manage memory edits subscreen',
	},
	editBlurbButton: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.edit-blurb-button',
		defaultMessage: 'Edit memory blurb',
		description: 'Accessible label for the floating button that opens the edit memory blurb input',
	},
	editBlurbInputPlaceholder: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.edit-blurb-input-placeholder',
		defaultMessage: 'Edit or add something to your profile memory…',
		description: 'Placeholder text shown inside the edit memory blurb text input',
	},
	submitEditBlurb: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.submit-edit-blurb',
		defaultMessage: 'Submit',
		description: 'Accessible label for the submit button in the edit memory blurb input',
	},
	addMemoryButton: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.add-memory-button',
		defaultMessage: 'Add new memory',
		description: 'Label for the footer button that opens the add memory input',
	},
	addMemoryHelperText: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.add-memory-helper-text',
		defaultMessage: 'Share a new detail with Rovo you want it to remember',
		description:
			'Helper text shown below the add memory input prompting the user to share a new detail with Rovo',
	},
	MemorySubscreenFooterAddMemoryPlaceholder: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.add-memory-input-placeholder',
		defaultMessage: 'In my role, my main responsibilities are...',
		description: 'Placeholder text shown inside the add memory text input',
	},
	submitAddMemory: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.submit-add-memory',
		defaultMessage: 'Submit',
		description: 'Accessible label for the submit button in the add memory input',
	},
	cancelAddMemory: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.cancel-add-memory',
		defaultMessage: 'Cancel',
		description: 'Label for the cancel button in the add memory input',
	},
	confirmAddMemory: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.confirm-add-memory',
		defaultMessage: 'Save',
		description: 'Label for the confirm button in the add memory input',
	},
	addSuccessMessage: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.add-success-message',
		defaultMessage: 'Memory successfully added.',
		description: 'Flag message shown after a memory is successfully added',
	},
	addErrorMessage: {
		id: 'ai-mate.rovo-memories.memory-modal-subscreen.add-error-message',
		defaultMessage: "We couldn't add this memory. Please try again.",
		description: 'Flag message shown when adding a memory fails',
	},
});
